import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { useToaster } from "hooks";
import { getCall_v2, postCall_v2 } from "services";
import { error, success } from "constants";
import { CREATE_SHIPMENT_API, GET_DO_SHIPMENT_DETAILS_API } from "./constants";
import { buildCreateShipmentPayload, validateShipmentForm } from "./makeData";
import { ShipmentDetails, DeliveryActions, DispatchOrders } from "./components";

const getInitialFormData = (email = "") => ({
  createdBy: email,
  deliveryType: "JOTS",
  modeOfShipment: "Road",
  ftStatus: "Planned",
  salesFreightValue: 0,
  salesFreightUOM: "INR",
  dispatchOrders: [],
});

const CreateShipment = () => {
  const triggerToaster = useToaster();
  const userData = useSelector((state) => state.userData);
  const userEmail = userData?.email || "";

  const [formData, setFormData] = useState(() => getInitialFormData(userEmail));
  const [searchOrderNumber, setSearchOrderNumber] = useState("");
  const [isSearchingOrder, setIsSearchingOrder] = useState(false);
  const [allDispatchOrders, setAllDispatchOrders] = useState([]);
  const [availablePsIds, setAvailablePsIds] = useState([]);
  const [selectedPsIds, setSelectedPsIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessAnim, setIsSuccessAnim] = useState(false);
  const [isDropCityMissing, setIsDropCityMissing] = useState(false);

  useEffect(() => {
    if (userEmail) {
      setFormData((prev) => ({
        ...prev,
        createdBy: userEmail,
      }));
    }
  }, [userEmail]);

  // Field change handlers
  const handleChangeField = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleDropCityChange = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      addressPickupAndDrop: {
        ...prev.addressPickupAndDrop,
        drop: {
          ...prev.addressPickupAndDrop?.drop,
          city: value,
        },
      },
    }));
  }, []);

  // Line item quantity handler
  const handleUpdateLineItem = useCallback((doIdx, itemIdx, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.dispatchOrders || [])];
      const targetDO = { ...updated[doIdx] };
      const updatedItems = [...(targetDO.lineItems || [])];
      updatedItems[itemIdx] = {
        ...updatedItems[itemIdx],
        [field]: value,
      };
      targetDO.lineItems = updatedItems;
      updated[doIdx] = targetDO;

      setAllDispatchOrders((prevAll) =>
        prevAll.map((item) => (item.doId === targetDO.doId ? targetDO : item)),
      );

      return {
        ...prev,
        dispatchOrders: updated,
      };
    });
  }, []);

  // Order Search handler
  const handleSearchOrder = useCallback(async () => {
    if (!searchOrderNumber?.trim()) {
      triggerToaster("Please enter an Order Number", error);
      return;
    }

    try {
      setIsSearchingOrder(true);
      const res = await getCall_v2(GET_DO_SHIPMENT_DETAILS_API, {
        orderNumber: searchOrderNumber.trim(),
      });
      const data = res?.data;

      if (!data) {
        triggerToaster("No shipment details found for this order", error);
        return;
      }

      const shippingAddress = data.shippingAddress || {};
      const firstPickUp = data.deliveryOrders?.[0]?.pickUp || {};
      const dropCity = (shippingAddress?.city || "").trim();
      setIsDropCityMissing(!dropCity);

      const pickupAddress = [
        firstPickUp.address_line_1,
        firstPickUp.address_line_2,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();

      const fetchedDOs = (data.deliveryOrders || []).map((dispatchOrder) => ({
        doId: dispatchOrder?.psIdentifier,
        lineItems: (dispatchOrder?.lineItems || []).map((item) => {
          const releasedQty = Number(item?.releasedQty) || 0;
          const usedQty = Number(item?.usedQty) || 0;
          const calculatedLimit = releasedQty * 1.2 - usedQty;
          const maxAllowedQty = Math.max(0, Number(calculatedLimit.toFixed(3)));

          return {
            lineItemNumber: item?.lineItemId,
            materialMasterId: item?.variantMmId || "",
            quantity: "",
            releasedQty,
            usedQty,
            maxAllowedQty,
            uom: item?.uom || "",
          };
        }),
      }));

      const psIds = fetchedDOs
        .map((dispatchOrder) => dispatchOrder.doId)
        .filter(Boolean);

      setAllDispatchOrders(fetchedDOs);
      setAvailablePsIds(psIds);
      setSelectedPsIds([]);

      setFormData((prev) => ({
        ...prev,
        dispatchOrders: [],
        addressPickupAndDrop: {
          pickup: {
            address: pickupAddress,
            city: firstPickUp?.city || "",
            state: firstPickUp?.state || "",
            postalCode: firstPickUp?.pincode || "",
          },
          drop: {
            address: shippingAddress.streetLine || "",
            city: dropCity,
            state: shippingAddress?.state || "",
            postalCode: shippingAddress?.postalCode || "",
          },
        },
      }));

      triggerToaster("Order details fetched successfully!", success);
    } catch (err) {
      const errMsg =
        err?.data?.errorMessage ||
        "Failed to fetch order details. Please try again.";
      triggerToaster(errMsg, error);
    } finally {
      setIsSearchingOrder(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOrderNumber]);

  // Selection handler for PS IDs from MultiSelectDropdown
  const handleSelectedPsIdsChange = useCallback(
    (newSelectedPsIds = []) => {
      setSelectedPsIds(newSelectedPsIds);
      const filteredDOs = allDispatchOrders.filter((dispatchOrder) =>
        newSelectedPsIds.includes(dispatchOrder?.doId),
      );
      setFormData((prev) => ({
        ...prev,
        dispatchOrders: filteredDOs,
      }));
    },
    [allDispatchOrders],
  );

  // Form actions
  const handleCancel = useCallback(() => {
    setFormData(getInitialFormData(userEmail));
    setSearchOrderNumber("");
    setAllDispatchOrders([]);
    setAvailablePsIds([]);
    setSelectedPsIds([]);
    setIsDropCityMissing(false);
    triggerToaster("Form reset successful", success);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const handleSubmit = useCallback(async () => {
    const { isValid, errors: validationErrors } =
      validateShipmentForm(formData);

    if (!isValid) {
      triggerToaster(validationErrors[0], error);
      return;
    }

    const payload = buildCreateShipmentPayload(formData);

    try {
      setIsSubmitting(true);
      const res = await postCall_v2(CREATE_SHIPMENT_API, payload);
      if (res) {
        setIsSuccessAnim(true);
        setTimeout(() => {
          setFormData(getInitialFormData(userEmail));
          setSearchOrderNumber("");
          setAllDispatchOrders([]);
          setAvailablePsIds([]);
          setSelectedPsIds([]);
          setIsDropCityMissing(false);
          setIsSuccessAnim(false);
          triggerToaster(
            res?.message || "Shipment created successfully",
            success,
          );
        }, 2000);
      }
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage ||
          "Something went wrong while creating shipment",
        error,
      );
    } finally {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, userEmail]);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          fontSize: "30px",
          letterSpacing: 0.3,
          ml: 3,
        }}
      >
        Create JOTS Shipment
      </Typography>

      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: "1600px",
          margin: "0 auto",
        }}
      >
        <DispatchOrders
          orderNumber={searchOrderNumber}
          onOrderNumberChange={setSearchOrderNumber}
          onSearchOrder={handleSearchOrder}
          isSearching={isSearchingOrder}
          availablePsIds={availablePsIds}
          selectedPsIds={selectedPsIds}
          onSelectedPsIdsChange={handleSelectedPsIdsChange}
          dispatchOrders={formData.dispatchOrders || []}
          onUpdateLineItem={handleUpdateLineItem}
        />

        <ShipmentDetails
          formData={formData}
          onChangeField={handleChangeField}
          showDropCity={isDropCityMissing}
          onChangeDropCity={handleDropCityChange}
        />

        <DeliveryActions
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          isSuccessAnim={isSuccessAnim}
        />
      </Box>
    </Box>
  );
};

export default CreateShipment;
